import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { WebSocketProvider, useWebSocket } from './context/WebSocketContext.js';
import { Navbar } from './components/Navbar.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { AdminPage } from './pages/AdminPage.js';
import { SuperAdminPage } from './pages/SuperAdminPage.js';
import { RefreshCw, ArrowLeft, Shield, ShieldAlert, AlertTriangle } from 'lucide-react';

const AppContent: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { currentAdminShopId, setCurrentAdminShopId } = useWebSocket();

  const [currentPath, setCurrentPath] = useState(window.location.pathname || '/');
  const [activeTab, setActiveTab] = useState('queue');
  const [showSimulateModal, setShowSimulateModal] = useState(false);

  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname || '/');
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
      setCurrentPath(path);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      if (currentPath === '/' || currentPath === '/login') {
        if (user.role === 'shopkeeper') navigate('/shop');
        else if (user.role === 'admin') navigate('/admin');
        else if (user.role === 'superadmin') navigate('/superadmin');
      }
    }
  }, [isAuthenticated, user, currentPath]);

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center gap-3 text-slate-400" style={{ background: 'var(--bg-primary)' }}>
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-emerald-400" />
        </div>
        <div>
          <div className="text-sm font-semibold text-white">Starting PrintBridge</div>
          <div className="text-[11px] text-slate-500 font-mono">Initializing system...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const portal = currentPath.startsWith('/superadmin')
      ? 'superadmin'
      : currentPath.startsWith('/admin')
      ? 'admin'
      : 'shop';
    return <LoginPage portal={portal} />;
  }

  // ── Access Denied Card ──
  const AccessDenied = ({ message, redirectLabel, redirectPath }: { message: string; redirectLabel: string; redirectPath: string }) => (
    <div className="max-w-lg mx-auto my-16 glass-card p-8 rounded-2xl border border-rose-500/25 text-center space-y-5 animate-scale-in">
      <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mx-auto text-rose-400">
        <ShieldAlert className="w-7 h-7" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-white">403 — Access Denied</h2>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">{message}</p>
        {currentPath === '/superadmin' && (
          <div className="mt-4 p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-xs text-purple-200 text-left space-y-1.5">
            <div className="font-bold text-purple-300 text-xs">Super Admin Root Access:</div>
            <div>• Email: <code className="bg-black/40 px-1.5 py-0.5 rounded font-mono text-white">superadmin@example.com</code></div>
            <div>• Password: <code className="bg-black/40 px-1.5 py-0.5 rounded font-mono text-white">admin1234</code></div>
            <div className="text-[11px] text-purple-300/80 pt-1">
              Tip: Click below to sign out and log in with the super admin account above!
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <button
          onClick={() => navigate(redirectPath)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {redirectLabel}
        </button>
        {currentPath === '/superadmin' && (
          <button
            onClick={() => { logout(); }}
            className="px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs shadow-lg shadow-purple-600/30 transition-all flex items-center gap-2 cursor-pointer"
          >
            <span>Sign Out & Log In as Super Admin</span>
          </button>
        )}
      </div>
    </div>
  );

  // ── Page Renderer ──
  const renderPage = () => {
    if (currentPath === '/shop') {
      if (user?.role === 'shopkeeper') {
        return <DashboardPage activeTab={activeTab} setActiveTab={setActiveTab} showSimulateModal={showSimulateModal} setShowSimulateModal={setShowSimulateModal} />;
      }
      if ((user?.role === 'admin' || user?.role === 'superadmin') && currentAdminShopId) {
        return <DashboardPage activeTab={activeTab} setActiveTab={setActiveTab} adminShopId={currentAdminShopId} showSimulateModal={showSimulateModal} setShowSimulateModal={setShowSimulateModal} />;
      }
      return (
        <div className="max-w-lg mx-auto my-16 glass-card p-8 rounded-2xl border border-amber-500/25 text-center space-y-5 animate-scale-in">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mx-auto text-amber-400">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">No Shop Selected</h2>
            <p className="text-sm text-slate-400 mt-2">As an administrator, select a shop from the admin panel first.</p>
          </div>
          <button onClick={() => navigate('/admin')} className="btn-primary">Go to Admin Panel</button>
        </div>
      );
    }

    if (currentPath === '/admin') {
      if (user?.role === 'admin' || user?.role === 'superadmin') {
        return <AdminPage onOpenShopDashboard={(shopId) => { setCurrentAdminShopId(shopId); navigate('/shop'); }} />;
      }
      return <AccessDenied message="The admin portal is restricted to platform administrators." redirectLabel="Return to Shop Terminal" redirectPath="/shop" />;
    }

    if (currentPath === '/superadmin') {
      if (user?.role === 'superadmin') return <SuperAdminPage />;
      return <AccessDenied message="The Super Admin portal requires root clearance." redirectLabel="Return to Your Portal" redirectPath={user?.role === 'admin' ? '/admin' : '/shop'} />;
    }

    // Fallback
    if (user?.role === 'shopkeeper') return <DashboardPage activeTab={activeTab} setActiveTab={setActiveTab} showSimulateModal={showSimulateModal} setShowSimulateModal={setShowSimulateModal} />;
    if (user?.role === 'superadmin') return <SuperAdminPage />;
    return <AdminPage onOpenShopDashboard={(shopId) => { setCurrentAdminShopId(shopId); navigate('/shop'); }} />;
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Admin View Banner */}
      {currentAdminShopId && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 text-xs text-amber-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" />
            <span className="font-semibold">Admin Viewing Shop {currentAdminShopId} — All actions are audit-logged</span>
          </div>
          <button
            onClick={() => { setCurrentAdminShopId(null); navigate('/admin'); }}
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs font-bold transition-all"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to Admin
          </button>
        </div>
      )}

      <Navbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenSimulateModal={() => setShowSimulateModal(true)}
        currentPath={currentPath}
        navigate={navigate}
      />

      <main className="flex-1 max-w-[1440px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {renderPage()}
      </main>

      <footer className="border-t border-white/05 py-3 text-center text-[11px] text-slate-600 font-mono">
        PrintBridge v1.0 Production · Zero-Cost WhatsApp Automation · 24h Queue Safeguard
      </footer>
    </div>
  );
};

export const App: React.FC = () => (
  <AuthProvider>
    <WebSocketProvider>
      <AppContent />
    </WebSocketProvider>
  </AuthProvider>
);

export default App;
